/**
 * **A goal to renew**: how far the client is along it — start, now, target on one bar — and the two
 * answers the card gives on the spot: more time (+2 or +4 weeks, `PATCH /customer/{id}/goal/{goalId}`)
 * or „Ukończ cel" (`…/complete`). Either makes `goalRenewalDue` stop being true, so the card is struck
 * once the server agreed (attention-queue R5, the implicit-close rule).
 *
 * The bar is `model/goalProgress`'s — the server's own percentage, and no bar where nothing was
 * measured: "Brak pomiaru" with the tab that takes the reading instead. On the desk (owner,
 * 26 September 2026) the goal's span from the day it was set to its deadline, and the series it is
 * judged on — the movement's trend for a lift goal, the body weight for a weight goal. An item
 * without `goalId` (from before the additive field) keeps the bar and draws no write.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { clientGoalQueryOptions, goalMeasurementTab, goalTypeOf } from '~/entities/client';
import { LiftTrendChart } from '~/entities/lift-history';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { MeasureMarkIcon } from '~/shared/ui/EmptyStateIcons';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';

import { useHomeFormats } from '../lib/useHomeFormats';
import type { TaskAct } from '../lib/useTaskAct';
import type { TaskDensity, TaskSlots } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { goalProgress, goalReading, goalTimeline } from '../model/goalProgress';
import { taskWriteTargets } from '../model/writeTargets';
import { BlockRead } from './BlockRead';
import { GoalWeightTrend } from './GoalWeightTrend';
import { ActButton, ActRefusal, ActRow, ActSpacer, Pick } from './TaskActs';
import styles from './taskBlocks.module.css';

/** The two terms the canon offers. */
const TERMS = [2, 4] as const;
const EXTEND = 'extend';
const COMPLETE = 'completeGoal';

interface GoalBlockProps {
  readonly item: AttentionItem;
  readonly density: TaskDensity;
  readonly todayIso: string;
  readonly slots: TaskSlots;
  readonly act: TaskAct;
  readonly reducedMotion: boolean;
}

export function GoalBlock({ item, density, todayIso, slots, act, reducedMotion }: GoalBlockProps) {
  const { t, i18n } = useTranslation();
  const { shortDate } = useHomeFormats();
  const contentLang = useContentLanguage();
  const customerId = item.subjectCustomerId;
  const goal = useQuery({ ...clientGoalQueryOptions(customerId ?? 0, contentLang), enabled: customerId !== null });
  const gate = useSkeletonGate(customerId !== null && goal.isPending);
  const [weeks, setWeeks] = useState<number>(TERMS[0]);
  const progress =
    goal.data == null ? null : goalProgress(goal.data, { t: (key, options) => t(key, options), locale: i18n.language });
  const deadline = progress?.deadline ?? item.triage.goalDeadline;
  // The desk's timeline and series; the Przegląd's card holds one height and keeps the bar alone.
  const timeline = density === 'desk' && goal.data != null ? goalTimeline(goal.data, todayIso) : null;
  const reading = goal.data == null ? ({ kind: 'none' } as const) : goalReading(goal.data);
  const description = goal.data?.description?.trim() || null;
  const gapTab = goal.data == null ? null : goalMeasurementTab(goalTypeOf(goal.data.type), goal.data.currentValue);
  const target = taskWriteTargets(item).goal;
  const writes = density === 'short' ? undefined : slots.writes;

  return (
    <div className={`${String(styles.stack)} ${String(styles[density])}`} aria-busy={gate.waiting}>
      <BlockRead gate={gate} reserve={styles.reserveGoal} failed={goal.isError} className={styles.stack}>
        {progress === null ? null : (
          <>
            <p className={styles.goalHead}>
              <span className={styles.goalTitle}>{progress.title}</span>
              {progress.remainingText === null ? null : (
                <span className={styles.note}>{t('web:tasks.blocks.goal.remaining', { value: progress.remainingText })}</span>
              )}
            </p>
            {progress.fraction !== null ? (
              <>
                <div
                  className={styles.goalBar}
                  style={{ '--goal': String(progress.fraction) } as CSSProperties}
                  role="img"
                  aria-label={t('web:tasks.blocks.goal.bar', { percent: Math.round(progress.fraction * 100) })}
                >
                  <span className={styles.goalFill} />
                  <span className={styles.goalMarker} />
                </div>
                <p className={styles.goalAxis}>
                  <span>{progress.startText === null ? '' : t('web:tasks.blocks.goal.start', { value: progress.startText })}</span>
                  <span className={styles.goalNow}>{progress.nowText === null ? '' : t('web:tasks.blocks.goal.now', { value: progress.nowText })}</span>
                  <span>{progress.targetText === null ? '' : t('web:tasks.blocks.goal.target', { value: progress.targetText })}</span>
                </p>
              </>
            ) : gapTab !== null && customerId !== null ? (
              // Nothing measured yet, and the reading has a home: the tab that takes it (the client
              // detail's own sentence and link, `entities/client`'s `goalMeasurementTab`).
              <EmptyState
                inline
                className={styles.note}
                mark={<MeasureMarkIcon />}
                tone="action"
                title={
                  <span>
                    {t('web:clientDetail.goal.unmeasured')}{' '}
                    <Link
                      className={inlineButton.inlineTextButton}
                      to="/klienci/$clientId"
                      params={{ clientId: String(customerId) }}
                      search={{ tab: gapTab }}
                    >
                      {t(`web:clientDetail.goal.unmeasuredLink.${gapTab}`)}
                    </Link>
                  </span>
                }
              />
            ) : null}
            {/* The trainer's own words about the goal, where they wrote any — a free-text goal's whole
              * content beside its span. */}
            {density === 'desk' && description !== null ? <p className={styles.goalDescription}>{description}</p> : null}
            {timeline === null ? null : (
              <>
                <div
                  className={`${String(styles.goalBar)} ${String(styles.timeline)}`}
                  style={{ '--goal': String(timeline.elapsed) } as CSSProperties}
                  role="img"
                  aria-label={t('web:tasks.blocks.goal.timeline', { percent: Math.round(timeline.elapsed * 100) })}
                >
                  <span className={styles.goalFill} />
                  <span className={styles.goalMarker} />
                </div>
                <p className={styles.goalAxis}>
                  <span>{t('web:tasks.blocks.goal.setOn', { date: shortDate(timeline.from) })}</span>
                  <span>{t('web:tasks.blocks.goal.deadlineOn', { date: shortDate(timeline.to) })}</span>
                </p>
              </>
            )}
          </>
        )}
      </BlockRead>

      {/* The series the goal is judged on, on the desk (owner, 26 September 2026). */}
      {/* Where nothing was measured, the sentence above already says so and names the tab. */}
      {density !== 'desk' || customerId === null || goal.data == null || gapTab !== null ? null : reading.kind === 'lift' ? (
        <LiftTrendChart clientId={customerId} exerciseId={reading.exerciseId} reducedMotion={reducedMotion} />
      ) : reading.kind === 'weight' ? (
        <GoalWeightTrend
          clientId={customerId}
          since={timeline?.from ?? goal.data.createdAt?.slice(0, 10) ?? null}
          better={(goal.data.targetValue ?? 0) < (goal.data.startValueSnapshot ?? 0) ? 'lower' : 'higher'}
          reducedMotion={reducedMotion}
        />
      ) : null}

      {target === null || deadline === null || writes === undefined ? null : (
        <>
          <ActRow label={t('web:tasks.blocks.goal.extendLabel')}>
            {TERMS.map((term) => (
              <Pick
                key={term}
                label={t('web:tasks.blocks.goal.term', { count: term })}
                on={weeks === term}
                onPick={() => {
                  setWeeks(term);
                }}
              />
            ))}
            <ActSpacer />
            <ActButton
              controller={act}
              tone="ghost"
              glyph="complete"
              label={t('web:tasks.blocks.acts.completeGoal')}
              run={{
                act: COMPLETE,
                closes: true,
                done: t('web:tasks.blocks.live.goalCompleted'),
                write: () => writes.completeGoal(target),
              }}
            />
            <ActButton
              controller={act}
              tone="ink"
              glyph="extend"
              label={t('web:tasks.blocks.acts.extend')}
              run={{
                act: EXTEND,
                closes: true,
                done: t('web:tasks.blocks.live.goalExtended', { count: weeks }),
                write: () => writes.extendGoal(target, deadline, weeks),
              }}
            />
          </ActRow>
          <ActRefusal controller={act} acts={[EXTEND, COMPLETE]} />
        </>
      )}
    </div>
  );
}
